import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGateValue } from '@statsig/react-bindings';
import {
  Alert,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Flex,
  Icon,
  type IconType,
  Markdown,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  Spinner,
  Text,
  toast,
} from '@kognitos/lattice';
import {
  type V2BookConnectionDescriptor,
  type V2BookConnectionStatus,
  V2BookConnectionConditionStatus,
  V2BookConnectionConditionType,
  V2BookConnectionState,
} from '@/generated/hooks/bdk/models';
import { formatRelativeTime } from '@/shared/utils/datetime-utils';
import { useConnectionDiagnosticsInsight } from '../hooks/use-connection-diagnostics-insight';
import {
  CONDITION_TYPE_I18N_KEYS,
  getConditionVisual,
  getFailedConditions,
  getLastScannedTime,
  parseInsightSections,
} from './connection-diagnostics-utils';

/**
 * The shifu-powered connection diagnostics (popover + edit-modal alert) are
 * gated behind the same flag as the integration assistant, since both rely on
 * the shifu Rain agent.
 */
const DIAGNOSTICS_GATE = 'enable_integration_assistant';

/**
 * Condition types surfaced to the user, in display order. Internal/technical
 * conditions (book validity, authentication plumbing, runtime version,
 * discovery/trigger/poll readiness) are intentionally hidden — they are not
 * actionable from this panel. All three are always shown (a missing/not-run
 * one renders as "skipped") so the "N of 3 passed" count is stable.
 */
const DISPLAYED_CONDITION_TYPES: V2BookConnectionConditionType[] = [
  V2BookConnectionConditionType.BOOK_CONNECTION_CONDITION_TYPE_CREDENTIALS_VALID,
  V2BookConnectionConditionType.BOOK_CONNECTION_CONDITION_TYPE_CONFIGURATION_VALID,
  V2BookConnectionConditionType.BOOK_CONNECTION_CONDITION_TYPE_DEPLOYMENT_READY,
];

type HealthState = 'healthy' | 'checking' | 'failed' | 'inactive';

/** Derives the panel state from the connection's overall status. */
function getHealthState(status?: V2BookConnectionStatus): HealthState {
  switch (status?.state) {
    case V2BookConnectionState.BOOK_CONNECTION_STATE_READY:
      return 'healthy';
    case V2BookConnectionState.BOOK_CONNECTION_STATE_FAILED:
    case V2BookConnectionState.BOOK_CONNECTION_STATE_CONDITIONS_NOT_MET:
      return 'failed';
    case V2BookConnectionState.BOOK_CONNECTION_STATE_INACTIVE:
      // Scaled to zero after inactivity — settled, not a failure and not
      // something the checks are still working through.
      return 'inactive';
    default:
      // PENDING / UNKNOWN / unset — still being validated.
      return 'checking';
  }
}

/**
 * Whether the status pill should offer the health popover: for pending and
 * failed connections (not healthy — a green connection needs no explanation).
 *
 * INACTIVE is excluded too. It isn't a health problem to diagnose: the checks
 * can't run against a pod scaled to zero, so the panel would show a stale
 * checklist, and this popover is feature-gated besides. ConnectionStatusTag
 * carries an ungated tooltip for that state instead.
 */
export function shouldShowHealthDetails(
  status?: V2BookConnectionStatus,
): boolean {
  if (status?.state == null) return false;
  const healthState = getHealthState(status);
  return healthState !== 'healthy' && healthState !== 'inactive';
}

const HEADER_BY_STATE: Record<
  HealthState,
  { icon: IconType; className: string; label: string }
> = {
  healthy: {
    icon: 'CircleCheck',
    className: 'text-success',
    label: 'integrations.connectionDiagnostics.healthyTitle',
  },
  checking: {
    icon: 'Loader',
    className: '',
    label: 'integrations.connectionDiagnostics.checkingTitle',
  },
  failed: {
    icon: 'AlertCircle',
    className: 'text-destructive',
    label: 'integrations.connectionDiagnostics.failedTitle',
  },
  inactive: {
    icon: 'Moon',
    className: 'text-muted-foreground',
    label: 'integrations.connectionDiagnostics.inactiveTitle',
  },
};

function getChecklistSummary(status?: V2BookConnectionStatus) {
  const byType = new Map(
    (status?.conditions ?? []).map(c => [c.type, c] as const),
  );
  const rows = DISPLAYED_CONDITION_TYPES.map(type => ({
    type,
    status: byType.get(type)?.status,
  }));
  const passed = rows.filter(
    r =>
      r.status ===
      V2BookConnectionConditionStatus.BOOK_CONNECTION_CONDITION_STATUS_TRUE,
  ).length;
  return { rows, total: rows.length, passed };
}

interface IInsightState {
  insight: string;
  isLoading: boolean;
  isError: boolean;
}

/** Raw failure text (condition reasons + overall reason) for the Copy action. */
function buildDetailsText(status?: V2BookConnectionStatus): string {
  const lines = getFailedConditions(status).map(c => {
    const reason = c.reason ?? 'no detail provided';
    const code = c.reason_code ? ` (${c.reason_code})` : '';
    return `${c.type}: ${reason}${code}`;
  });
  if (status?.reason) lines.push(status.reason);
  return lines.join('\n');
}

/** Renders shifu markdown with the wrapping rules for long code/URLs. */
function InsightMarkdown({ children }: { children: string }) {
  return (
    <Flex
      direction="column"
      className="min-w-0 [overflow-wrap:anywhere] [&_code]:[overflow-wrap:anywhere] [&_code]:[box-decoration-break:clone] [&_pre]:whitespace-pre-wrap [&_pre]:break-all [&_pre]:max-w-full [&_pre]:overflow-x-auto"
    >
      <Markdown
        textProps={{ level: 'small', color: 'muted', weight: 'normal' }}
      >
        {children}
      </Markdown>
    </Flex>
  );
}

/**
 * The shifu-generated explanation of the failure. Streams in as it generates;
 * falls back to a short message if the agent cannot answer. Presentational —
 * the fetch is owned by the parent so it can be warmed up early (e.g. on hover).
 * Shown as two sections: the cause (under the panel's "What went wrong" header)
 * and a "Potential fixes" section when the agent provides one.
 */
function ConnectionInsight({ insight, isLoading, isError }: IInsightState) {
  const { t } = useTranslation('integrations');

  if (insight) {
    const { cause, fixes } = parseInsightSections(insight);
    return (
      <Flex direction="column" gap={3} className="w-full">
        {cause && (
          <Flex direction="column" gap={1} className="w-full">
            <Text level="small" weight="medium">
              {t('integrations.connectionDiagnostics.insightTitle')}
            </Text>
            <InsightMarkdown>{cause}</InsightMarkdown>
          </Flex>
        )}
        {fixes && (
          <Flex direction="column" gap={1} className="w-full">
            <Text level="small" weight="medium">
              {t('integrations.connectionDiagnostics.fixesTitle')}
            </Text>
            <InsightMarkdown>{fixes}</InsightMarkdown>
          </Flex>
        )}
      </Flex>
    );
  }
  if (isLoading) {
    return (
      <Flex align="center" gap={2}>
        <Spinner size="sm" />
        <Text level="small" color="muted">
          {t('integrations.connectionDiagnostics.analyzing')}
        </Text>
      </Flex>
    );
  }
  if (isError) {
    return (
      <Text level="small" color="muted">
        {t('integrations.connectionDiagnostics.insightError')}
      </Text>
    );
  }
  return null;
}

/** Small "Copy details" action that copies the raw error to the clipboard. */
function CopyDetailsButton({ status }: { status?: V2BookConnectionStatus }) {
  const { t } = useTranslation('integrations');
  const details = buildDetailsText(status);
  if (!details) return null;

  return (
    <Button
      variant="ghost"
      size="sm"
      className="text-muted-foreground h-6 w-fit gap-1 px-1"
      onClick={() => {
        // Only claim success once the write actually resolves — the clipboard
        // API can be unavailable (insecure context) or denied by permission.
        const clipboard = navigator.clipboard;
        if (!clipboard) {
          toast.error(
            t('integrations.connectionDiagnostics.detailsCopyFailed'),
          );
          return;
        }
        clipboard
          .writeText(details)
          .then(() =>
            toast.success(
              t('integrations.connectionDiagnostics.detailsCopied'),
            ),
          )
          .catch(() =>
            toast.error(
              t('integrations.connectionDiagnostics.detailsCopyFailed'),
            ),
          );
      }}
    >
      <Icon type="Copy" size="sm" />
      {t('integrations.connectionDiagnostics.copyDetails')}
    </Button>
  );
}

/**
 * The shared failure body — a "Connection failed" header, the health checklist
 * with an "N of N passed" count, and the shifu diagnosis + Copy details. Used
 * verbatim by both the popover and the inline alert so the two stay consistent.
 */
export function ConnectionDiagnosticsDetail({
  connection,
  insight = '',
  isLoading = false,
  isError = false,
  collapsibleAnalysis = false,
  showAnalysis = true,
}: {
  connection?: V2BookConnectionDescriptor;
  /**
   * When true, the "What went wrong" analysis (cause + fixes) is tucked behind
   * a collapsed-by-default toggle so it doesn't push a form down. Used by the
   * inline dialog panels; the popover leaves it always expanded.
   */
  collapsibleAnalysis?: boolean;
  /** When false, only the checklist is shown (no analysis or Copy details). */
  showAnalysis?: boolean;
} & Partial<IInsightState>) {
  const { t } = useTranslation('integrations');

  const status: V2BookConnectionStatus | undefined = connection?.status;
  const { rows, total, passed } = getChecklistSummary(status);
  const lastScanned = getLastScannedTime(status);
  const healthState = getHealthState(status);
  const header = HEADER_BY_STATE[healthState];

  return (
    <Flex direction="column" gap={3} className="w-full min-w-64">
      {/* Title + timestamp. flex-wrap lets the timestamp drop to its own line
          on narrow widths (the popover) instead of clipping, while the title
          stays on one line. */}
      <Flex align="center" className="w-full flex-wrap gap-x-3 gap-y-1">
        <Flex align="center" gap={2} className="min-w-0">
          {healthState === 'checking' ? (
            <Spinner size="sm" />
          ) : (
            <Icon type={header.icon} size="sm" className={header.className} />
          )}
          <Text
            level="small"
            weight="medium"
            className={`whitespace-nowrap ${header.className}`}
          >
            {t(header.label)}
          </Text>
        </Flex>
        {lastScanned && healthState !== 'checking' && (
          <Text level="small" color="muted" className="whitespace-nowrap">
            {t('integrations.connectionDiagnostics.lastChecked', {
              time: formatRelativeTime(lastScanned),
            })}
          </Text>
        )}
      </Flex>

      <Flex direction="column" gap={2} className="w-full">
        {healthState !== 'checking' && (
          <Text level="small" color="muted">
            {healthState === 'inactive'
              ? t('integrations.connectionDiagnostics.inactiveDescription')
              : healthState === 'healthy'
                ? t('integrations.connectionDiagnostics.checksAllPassed', {
                    total,
                  })
                : t('integrations.connectionDiagnostics.checksPassed', {
                    passed,
                    total,
                  })}
          </Text>
        )}
        {rows.map(row => {
          // While the connection is still being validated, a check that hasn't
          // reported yet (missing or UNKNOWN) is in progress — show a loader
          // rather than the "skipped" dash, so the whole checklist reads as
          // pending instead of one row looking oddly skipped.
          const effectiveStatus =
            healthState === 'checking' &&
            (row.status == null ||
              row.status ===
                V2BookConnectionConditionStatus.BOOK_CONNECTION_CONDITION_STATUS_UNKNOWN)
              ? V2BookConnectionConditionStatus.BOOK_CONNECTION_CONDITION_STATUS_PENDING
              : row.status;
          const visual = getConditionVisual(effectiveStatus);
          const typeKey = CONDITION_TYPE_I18N_KEYS[row.type];
          return (
            <Flex key={row.type} align="center" gap={2}>
              {visual.isLoading ? (
                <Spinner size="sm" className={visual.className} />
              ) : (
                <Icon
                  type={visual.icon}
                  size="sm"
                  className={visual.className}
                />
              )}
              <Text level="small">{typeKey ? t(typeKey) : row.type}</Text>
            </Flex>
          );
        })}
      </Flex>

      {healthState === 'failed' && showAnalysis && (
        <>
          <Separator />
          {collapsibleAnalysis ? (
            <Collapsible className="w-full">
              <CollapsibleTrigger className="group flex w-full items-center gap-1 text-left">
                <Icon
                  type="ChevronRight"
                  size="sm"
                  className="text-muted-foreground transition-transform group-data-[state=open]:rotate-90"
                />
                <Text level="small" weight="medium">
                  {t('integrations.connectionDiagnostics.moreDetailsTitle')}
                </Text>
              </CollapsibleTrigger>
              <CollapsibleContent>
                {/* Indented so the two subsections read as detail nested under
                    the toggle rather than competing with it. */}
                <Flex direction="column" gap={3} className="w-full pt-2 pl-6">
                  <ConnectionInsight
                    insight={insight}
                    isLoading={isLoading}
                    isError={isError}
                  />
                  <CopyDetailsButton status={status} />
                </Flex>
              </CollapsibleContent>
            </Collapsible>
          ) : (
            <Flex direction="column" gap={3} className="w-full">
              <ConnectionInsight
                insight={insight}
                isLoading={isLoading}
                isError={isError}
              />
              <CopyDetailsButton status={status} />
            </Flex>
          )}
        </>
      )}
    </Flex>
  );
}

/**
 * Wraps a trigger (typically the status pill) in a Popover that reveals the
 * connection diagnostics on click. Works for any book integration.
 */
export function ConnectionDiagnosticsPopover({
  connection,
  displayName,
  showAnalysis = true,
  children,
}: {
  connection?: V2BookConnectionDescriptor;
  displayName?: string;
  /** When false, the popover shows only the checklist and skips the shifu fetch. */
  showAnalysis?: boolean;
  children: ReactNode;
}) {
  const diagnosticsEnabled = useGateValue(DIAGNOSTICS_GATE);
  const [open, setOpen] = useState(false);
  // Warm the request as soon as the user hovers the pill, so the answer is
  // usually already streaming (or cached) by the time the popover opens.
  const [warm, setWarm] = useState(false);
  const insightState = useConnectionDiagnosticsInsight({
    connection,
    displayName,
    enabled: diagnosticsEnabled && showAnalysis && (warm || open),
  });

  // Feature-gated: without the flag, render the trigger (status pill) alone —
  // no interactive wrapper, so it isn't announced as a button that does nothing.
  if (!diagnosticsEnabled) return <>{children}</>;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* PopoverTrigger renders a real <button> (native focus + Enter/Space),
          so we avoid a raw span+role=button wrapper. `text-left` keeps the
          wrapped pill from being centered like default button text.

          Activation is kept from bubbling: the pill is often nested in a
          clickable row (the connection card) whose handler would open a sheet
          over the popover the user just asked for. Enter/Space are stopped as
          well as the click, so keyboard users get the same behavior, while
          other keys still reach the row for list navigation. */}
      <PopoverTrigger
        onMouseEnter={() => setWarm(true)}
        onClick={event => event.stopPropagation()}
        onKeyDown={event => {
          if (event.key === 'Enter' || event.key === ' ')
            event.stopPropagation();
        }}
        className="inline-flex cursor-pointer text-left"
      >
        {children}
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        className="max-w-sm max-h-[var(--radix-popover-content-available-height)] overflow-y-auto overflow-x-hidden"
      >
        <ConnectionDiagnosticsDetail
          connection={connection}
          showAnalysis={showAnalysis}
          {...insightState}
        />
      </PopoverContent>
    </Popover>
  );
}

/**
 * Inline failure panel shown above the connection form (add + edit dialogs).
 * Renders the same content as the popover, wrapped in a destructive frame.
 * Returns null when there is no failing condition.
 */
export function ConnectionFailureAlert({
  connection,
  displayName,
}: {
  connection?: V2BookConnectionDescriptor;
  displayName?: string;
}) {
  const diagnosticsEnabled = useGateValue(DIAGNOSTICS_GATE);
  const hasFailure = hasConnectionFailure(connection?.status);
  const insightState = useConnectionDiagnosticsInsight({
    connection,
    displayName,
    enabled: diagnosticsEnabled && hasFailure,
  });
  if (!diagnosticsEnabled || !hasFailure) return null;

  // `block` overrides Alert's default `grid grid-cols-[0_1fr]` (tailwind-merge
  // keeps the last display utility): our content isn't an AlertTitle/Description
  // pair, so without this it lands in the 0-width first column and collapses to
  // its min-width instead of filling the panel.
  return (
    <Alert variant="destructive" className="block">
      <ConnectionDiagnosticsDetail
        connection={connection}
        collapsibleAnalysis
        {...insightState}
      />
    </Alert>
  );
}

/** True once the connection's checks have finished (no longer validating). */
export function isHealthCheckSettled(status?: V2BookConnectionStatus): boolean {
  return getHealthState(status) !== 'checking';
}

/** True when the connection has at least one failing condition. */
export function hasConnectionFailure(status?: V2BookConnectionStatus): boolean {
  return getFailedConditions(status).length > 0;
}

/**
 * True when the connection reports any condition data worth showing. Used to
 * decide whether to surface the full diagnostics panel (e.g. on the edit
 * dialog) — connections without granular conditions get no panel rather than
 * an empty "checks" list.
 */
export function hasConnectionDiagnostics(
  status?: V2BookConnectionStatus,
): boolean {
  return (status?.conditions?.length ?? 0) > 0;
}
